import QRCode from 'qrcode';
import { buildQrPayload } from './qr-payload.js';
import { createPairingSession, getPairingSessionStatus } from '../api/pairing.js';
import { setDeviceToken, setDeviceMeta } from '../state/device-store.js';
import { APP_VERSION } from '../config.js';

export const PAIRING_POLL_INTERVAL_MS = 3000;

const FINGERPRINT_KEY = 'signedge:deviceFingerprint';

function getOrCreateFingerprint() {
  let fp = localStorage.getItem(FINGERPRINT_KEY);
  if (!fp) {
    fp = crypto.randomUUID();
    localStorage.setItem(FINGERPRINT_KEY, fp);
  }
  return fp;
}

async function defaultCreateSession() {
  return createPairingSession({
    fingerprint: getOrCreateFingerprint(),
    userAgent: navigator.userAgent,
    appVersion: APP_VERSION,
    reportedScreenResolution: {
      width: window.screen.width,
      height: window.screen.height,
    },
  });
}

async function defaultGenerateQr(text, canvas) {
  await QRCode.toCanvas(canvas, text, {
    width: 256,
    margin: 2,
    color: { dark: '#000', light: '#fff' },
  });
}

function formatCode(code) {
  return code.split('').join(' ');
}

/**
 * Render inverted pairing screen: 6-char code + QR, poll session status.
 * @returns {() => void} cleanup (stop polling)
 */
export async function renderDisplayCode(
  rootEl,
  {
    createSession = defaultCreateSession,
    getStatus = getPairingSessionStatus,
    generateQr = defaultGenerateQr,
    onPaired = () => window.location.reload(),
    pollIntervalMs = PAIRING_POLL_INTERVAL_MS,
  } = {},
) {
  let pollTimer = null;
  let sessionId = null;
  let aborted = false;

  function stopPolling() {
    if (pollTimer !== null) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  function startPolling() {
    stopPolling();
    pollTimer = setInterval(async () => {
      if (!sessionId || aborted) return;
      try {
        const result = await getStatus(sessionId);
        if (result.status === 'consumed' && result.deviceToken) {
          stopPolling();
          setDeviceToken(result.deviceToken);
          if (result.deviceId) {
            setDeviceMeta({
              deviceId: result.deviceId,
              pairedAt: new Date().toISOString(),
            });
          }
          onPaired();
        } else if (result.status === 'expired') {
          await refreshSession();
        }
      } catch {
        /* keep polling on transient errors */
      }
    }, pollIntervalMs);
  }

  async function showSession(session) {
    sessionId = session.sessionId;
    const { code, expiresAt } = session;

    rootEl.innerHTML = `
      <div class="pairing pairing--display">
        <h1>Accoppia il TV</h1>
        <p class="pairing__hint">Inserisci il codice o scansiona il QR nel backoffice</p>
        <div class="pairing-display">
          <div class="pairing-display__code" aria-label="Codice accoppiamento">${formatCode(code)}</div>
          <canvas id="pair-qr" class="pairing-display__qr" aria-label="QR code accoppiamento"></canvas>
        </div>
        <p class="pairing-display__expires" id="pair-expires"></p>
        <p class="pairing-display__status" id="pair-status" role="status"></p>
      </div>
    `;

    const canvas = rootEl.querySelector('#pair-qr');
    const payload = buildQrPayload({ v: 1, code, sessionId });
    await generateQr(payload, canvas);

    const expiresEl = rootEl.querySelector('#pair-expires');
    if (expiresEl && expiresAt) {
      expiresEl.textContent = `Valido fino alle ${new Date(expiresAt).toLocaleTimeString()}`;
    }

    startPolling();
  }

  async function refreshSession() {
    stopPolling();
    const statusEl = rootEl.querySelector('#pair-status');
    if (statusEl) statusEl.textContent = 'Sessione scaduta, rigenerazione…';
    const session = await createSession();
    if (!aborted) await showSession(session);
  }

  const session = await createSession();
  if (!aborted) await showSession(session);

  return () => {
    aborted = true;
    stopPolling();
  };
}
